import { memo, useCallback, useEffect, useMemo } from "react"
import MoonIcon from "@/shared/icons/totodoicons/solid/moon.svg?react"
import type { ViewItem } from "../hooks/useViewData"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { useTaskActions } from "@/features/tasks/hooks/useTaskActions"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import { useViewContext } from "@/app/stores/viewContext"
import { cn } from "@/shared/utils/general/cn"
import { useTranslation } from "react-i18next"
import { computeGroupPositions } from "../utils/groupPositions"
import { useSetting } from "@/features/settings/hooks/useSetting"

// ---------------------------------------------------------------------------
// Evening section — subsection at the bottom of TodayPage.
//
// Matches v1's TotalLater component:
// - Auto-hides when there are zero evening tasks
// - Section header with moon icon + "This evening" label + border separator
// - Standard task list below (DnD-ready in future)
// ---------------------------------------------------------------------------

interface EveningSectionProps {
  items: ViewItem[]
}

/**
 * Evening subsection rendered inside TodayPage — not a standalone page.
 * Returns null when there are no evening tasks (auto-hides).
 */
export const EveningSection = memo(function EveningSection({
  items,
}: EveningSectionProps) {
  const { t } = useTranslation()
  const { completeTask, uncompleteTask } = useTaskActions()
  const { activeTaskId, isEditingTitle, openTask, closeTask } = useActiveTask()
  const setEveningTaskIds = useViewContext((s) => s.setEveningTaskIds)
  const setEveningGroupedAllTaskIds = useViewContext(
    (s) => s.setEveningGroupedAllTaskIds,
  )
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)

  // After evening hour, the section shows tomorrow's tasks → label changes
  const isPastEveningHour = new Date().getHours() >= eveningHour
  const eveningLabel = isPastEveningHour
    ? t("Tomorrow evening")
    : t("This evening")

  // Push evening task IDs into the global selection system
  const eveningIds = useMemo(
    () =>
      items
        .map((vi) => vi.task.id)
        .filter((id): id is string => id != null && id !== ""),
    [items],
  )

  useEffect(() => {
    setEveningTaskIds(eveningIds)
    if (eveningIds.length > 0) {
      setEveningGroupedAllTaskIds([eveningIds])
    } else {
      setEveningGroupedAllTaskIds([])
    }
    return () => {
      // Cleanup when unmounting (navigating away from TodayPage)
      setEveningTaskIds([])
      setEveningGroupedAllTaskIds([])
    }
  }, [eveningIds, setEveningTaskIds, setEveningGroupedAllTaskIds])

  // DnD drop highlight — narrow selector re-renders only when this zone's
  // status actually changes (not for other sidebar targets).
  const isDropHighlighted = useViewContext(
    (s) => s.isDragging && s.sidebarDropTarget === "this-evening",
  )

  const handleComplete = useCallback(
    (id: string) => completeTask(id),
    [completeTask],
  )
  const handleUncomplete = useCallback(
    (id: string) => uncompleteTask(id),
    [uncompleteTask],
  )

  // Show the section when it has items OR when being dragged over (drop zone)
  if (items.length === 0 && !isDropHighlighted) return null

  return (
    <div
      className={cn(
        "mt-7 rounded-xl transition-colors duration-100",
        isDropHighlighted && "bg-primary/10",
      )}
      data-sidebar-drop="this-evening"
    >
      {/* Section header — evening icon + label + separator */}
      <div className="mb-1">
        <div className="flex items-center gap-2 px-3 py-[5px] pb-2.5">
          <MoonIcon className="size-5 shrink-0 text-[#4A90D9]" />
          <span className="text-sm font-medium">{eveningLabel}</span>
        </div>
        <div className="mx-3 border-b border-foreground/16" />
      </div>

      {items.length > 0 ? (
        <EveningTaskList
          items={items}
          activeTaskId={activeTaskId}
          isEditingTitle={isEditingTitle}
          onComplete={handleComplete}
          onUncomplete={handleUncomplete}
          openTask={openTask}
          closeTask={closeTask}
        />
      ) : (
        /* Empty drop zone placeholder — visible only during drag-over */
        <div className="flex h-10 items-center justify-center rounded-xl border border-dashed border-primary/30 text-sm text-muted-foreground">
          {t("Drop here")}
        </div>
      )}
    </div>
  )
})

/** Inner list with section-aware group positions */
function EveningTaskList({
  items,
  activeTaskId,
  isEditingTitle,
  onComplete,
  onUncomplete,
  openTask,
  closeTask,
}: {
  items: ViewItem[]
  activeTaskId: string | null
  isEditingTitle: boolean
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  openTask: (id: string) => void
  closeTask: () => void
}) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { firsts, lasts } = useMemo(() => computeGroupPositions(items), [items])

  return (
    <div>
      <div className="flex flex-col gap-0.5">
        {items.map((item, idx) => {
          const taskId = item.task.id ?? ""
          const isActive = taskId === activeTaskId
          return (
            <TaskItem
              key={taskId}
              task={item.task}
              onComplete={onComplete}
              onUncomplete={onUncomplete}
              isActive={isActive}
              startAtData={item.startAtData}
              editTitle={isActive && isEditingTitle}
              onOpen={openTask}
              onClose={closeTask}
              isFirstInGroup={firsts[idx]}
              isLastInGroup={lasts[idx]}
            />
          )
        })}
      </div>
    </div>
  )
}
